/**
 * Authentication Method Helper Functions
 *
 * Utility functions for working with Microsoft Entra ID (Azure AD) authentication methods.
 */

import type { AuthMethodType, BatchAuthMethodsResponse } from '../types/authenticationMethods';

/**
 * Check if a user has a specific authentication method configured.
 *
 * @param userId - User's Azure AD object ID
 * @param methodType - Authentication method type to check for
 * @param authData - Batch authentication methods response data
 * @returns true if user has the method, false otherwise
 */
export function hasAuthenticationMethod(
  userId: string,
  methodType: AuthMethodType,
  authData: BatchAuthMethodsResponse | null
): boolean {
  if (!authData || !authData.methods[userId]) {
    return false;
  }

  const userMethods = authData.methods[userId];
  return userMethods.some(method => method['@odata.type'] === methodType);
}

/**
 * Count the number of instances of a specific authentication method for a user.
 *
 * @param userId - User's Azure AD object ID
 * @param methodType - Authentication method type to count
 * @param authData - Batch authentication methods response data
 * @returns Number of registered instances of the method
 */
export function countAuthenticationMethods(
  userId: string,
  methodType: AuthMethodType,
  authData: BatchAuthMethodsResponse | null
): number {
  if (!authData || !authData.methods[userId]) {
    return 0;
  }

  const userMethods = authData.methods[userId];
  return userMethods.filter(method => method['@odata.type'] === methodType).length;
}
